import React, { useState } from 'react';
import { AuthenticatedTemplate } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { ExpandLess, ExpandMore, Add as CreateIcon, Search as LookupIcon, Dashboard as DashboardIcon } from '@mui/icons-material';
import { Collapse, List, ListItemButton, ListItemIcon, ListItemText, Chip, Box } from '@mui/material';
import { useAppSelector } from '../../../../../app/hooks';
import { useGetMyProfileQuery } from '../../../../../services/ProfileService';
import { hasRefundsAccess } from '../../../../profile/utils/profileUtils';

// Types
interface RefundMenuItemConfig {
    id: string;
    title: string;
    icon: React.ComponentType<{ sx?: any }>;
    route: string;
    showCount?: boolean;
}

// Constants
const MENU_STYLES = {
    navList: { 
        border: 'black solid 1px' 
    },
    listItem: { 
        marginTop: '0px' 
    },
    nestedListItem: { 
        marginTop: '0px',
        paddingLeft: '48px' // Indent nested items
    },
    icon: { 
        color: 'white' 
    },
    countContainer: { 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        width: '100%' 
    },
    countChip: {
        backgroundColor: '#ef4444',
        color: 'white',
        fontSize: '0.75rem',
        minWidth: '24px',
        height: '20px'
    }
} as const;

const REFUND_MENU_ITEMS: RefundMenuItemConfig[] = [
    {
        id: 'dashboard',
        title: 'Dashboard',
        icon: DashboardIcon,
        route: '/refunds/dashboard',
        showCount: false
    },
    {
        id: 'create',
        title: 'Create',
        icon: CreateIcon,
        route: '/refunds/create',
        showCount: false
    },
    {
        id: 'lookup',
        title: 'Lookup',
        icon: LookupIcon,
        route: '/refunds/lookup',
        showCount: false
    }
];

// Sub-components
interface RefundMenuItemProps {
    item: RefundMenuItemConfig;
    onClick: () => void;
    count?: number;
    disabled?: boolean;
}

const RefundMenuItem: React.FC<RefundMenuItemProps> = ({ item, onClick, count, disabled = false }) => {
    const { title, icon: Icon, showCount } = item;
    const shouldShowCount = showCount && count && count > 0;

    return (
        <ListItemButton 
            sx={{
                ...MENU_STYLES.nestedListItem,
                opacity: disabled ? 0.5 : 1,
                cursor: disabled ? 'not-allowed' : 'pointer'
            }} 
            onClick={disabled ? undefined : onClick}
            disabled={disabled}
        >
            <ListItemIcon>
                <Icon sx={MENU_STYLES.icon} />
            </ListItemIcon>
            {shouldShowCount ? (
                <Box sx={MENU_STYLES.countContainer}>
                    <ListItemText primary={title} />
                    <Chip 
                        label={count} 
                        size="small" 
                        sx={MENU_STYLES.countChip}
                    />
                </Box>
            ) : (
                <ListItemText primary={title} />
            )}
        </ListItemButton>
    );
};

// Main component
export const RefundMenu: React.FC = () => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    
    const selectedCenter = useAppSelector(state => state.center.selectedCenter);
    const { data: profile } = useGetMyProfileQuery();
    const company = profile?.organization || '';

    const handleItemClick = (item: RefundMenuItemConfig): void => {
        // For lookup route, add company and center parameters
        if (item.route === '/refunds/lookup') {
            if (!company || !selectedCenter) {
                console.warn('Cannot navigate to refund lookup: missing required parameters', {
                    company: company || 'missing',
                    selectedCenter: selectedCenter || 'missing'
                });
                return;
            }
        }

        // For dashboard route, add company and center parameters
        if (item.route === '/refunds/dashboard') {
            if (!company || !selectedCenter) {
                console.warn('Cannot navigate to dashboard: missing required parameters', {
                    company: company || 'missing',
                    selectedCenter: selectedCenter || 'missing'
                });
                return;
            }
        }

        const params = new URLSearchParams();
        if (company) params.append('company', company);
        if (selectedCenter) params.append('center', selectedCenter);
        
        const routeWithParams = `${item.route}?${params.toString()}`;
        navigate(routeWithParams);
    };

    const toggleMenu = (): void => setOpen(!open);

    // Only show if user has Refunds claim access
    if (!hasRefundsAccess(profile)) {
        return null;
    }

    return (
        <AuthenticatedTemplate>
            <List component="nav" sx={MENU_STYLES.navList}>
                <ListItemButton onClick={toggleMenu}>
                    <ListItemText primary="Refunds" />
                    {open ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={open} timeout="auto" unmountOnExit>
                    {REFUND_MENU_ITEMS.map((item) => {
                        // Disable lookup and dashboard items if company profile hasn't loaded yet
                        const isDisabled = (item.route === '/refunds/lookup' || item.route === '/refunds/dashboard') && !company;
                        
                        return (
                            <RefundMenuItem
                                key={item.id}
                                item={item}
                                onClick={() => handleItemClick(item)}
                                disabled={isDisabled}
                            />
                        );
                    })}
                </Collapse>
            </List>
        </AuthenticatedTemplate>
    );
};
